import React, { useContext, useEffect, useState } from 'react';
import { Paper, useMediaQuery, useTheme } from '@material-ui/core';
import Cancel from '@material-ui/icons/Cancel';
import CheckCircle from '@material-ui/icons/CheckCircle';
import Edit from '@material-ui/icons/Edit';
import { Button } from '@michelin/acid-components';
import IconButton from '@michelin/acid-components/build/components/IconButton/IconButton';
import { useTranslation } from '@michelin/central-provider';
import Taxes from 'compositions/OrderDetail/components/PartsAndServices/Taxes';
import { WizardContext } from 'compositions/Wizard/WizardContext';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { ReactComponent as Tire } from 'icons/tires.svg';
import { PartPrices, PqlPriceType } from 'types/UFOTypes';
import { PQL_MOUNTING_WHEEL } from 'utils/constantValues';
import { BREAKPOINT } from 'utils/responsivenessUtils';
import { getTAXES, getTotalPartTAX } from 'utils/taxes';
import type { SelectedMountingOptionType } from './MountingOptionsModal';
import { RequestPql } from './RequestPql';

function instanceOfPqlPrices(price: PqlPriceType | PartPrices): price is PqlPriceType {
  return 'amount' in price;
}

interface InstallationDetailProps {
  option: SelectedMountingOptionType;
  onOpen: () => void;
  comboNumber: number;
  qtySelectorComponent?: React.ReactNode;
  deleteOptionComponent?: React.ReactNode;
  prices: { installation?: PartPrices; tpms?: PartPrices; wheels?: PqlPriceType | PartPrices };
  readonly?: boolean;
  orderWheelPQL?: number;
  setOrderWheelPQL?: (mspm: string, price: number) => void;
  relatedTireMspn: string;
  reqPqlForm: boolean;
  setReqPqlForm: (v: boolean) => void;
}

export function InstallationDetail(props: InstallationDetailProps) {
  const {
    relatedTireMspn,
    option,
    onOpen,
    comboNumber,
    qtySelectorComponent,
    deleteOptionComponent,
    prices,
    readonly,
    orderWheelPQL,
    setOrderWheelPQL,
    reqPqlForm,
    setReqPqlForm,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const isDealer = useCurrentLocationIsDealer();
  const mobileView = useMediaQuery(theme.breakpoints.down(BREAKPOINT));

  const { getPql, contextPqlPrices } = useContext(WizardContext);

  const wheelMspn = PQL_MOUNTING_WHEEL;

  let wheelPql = 0;
  if (prices.wheels?.amount) wheelPql = prices.wheels.amount;
  else if (prices.wheels?.invoice_price) wheelPql = Number(prices.wheels.invoice_price);
  else if (orderWheelPQL) wheelPql = orderWheelPQL;
  else if (getPql) wheelPql = getPql(wheelMspn, 0, relatedTireMspn) || 0;

  const [pqlPrice, setPqlPrice] = useState<number | null>(wheelPql);

  useEffect(() => {
    if (getPql && getPql(wheelMspn, 0, relatedTireMspn) !== pqlPrice) {
      setPqlPrice(getPql(wheelMspn, 0, relatedTireMspn));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextPqlPrices]);

  let total = 0;
  if (option.installation) total += Number(prices.installation?.invoice_price) * option.qty;
  if (option.tpms) total += Number(prices.tpms?.invoice_price) * option.qty;
  if (option.wheels) total += Number(prices.wheels?.unitAmout || prices.wheels?.invoice_price) * option.qty;
  const missingPrice =
    (option.installation && !prices.installation?.invoice_price) ||
    (option.tpms && !prices.tpms?.invoice_price) ||
    (option.wheels && !prices.wheels?.unitAmout && !prices.wheels?.invoice_price);

  if (reqPqlForm) {
    return (
      <RequestPql
        readonly={readonly}
        mobileView={mobileView}
        relatedTireMspn={relatedTireMspn}
        setOrderWheelPQL={setOrderWheelPQL}
        onClick={() => setReqPqlForm(false)}
      />
    );
  }

  const requestPql = (
    <Button
      size="small"
      variant="contained"
      color="warning"
      onClick={() => setReqPqlForm(true)}
      style={{
        height: 13,
        width: 74,
        fontSize: 8,
        whiteSpace: 'nowrap',
      }}
    >
      {t('Provide Price')}
    </Button>
  );

  const pqlPriceRender = (
    <div style={{ marginLeft: 8, marginRight: 0 }}>
      {t(`$${(Number(pqlPrice) * option.qty).toFixed(2)}`)}
      {isDealer && !readonly && (
        <IconButton
          size="small"
          onClick={() => setReqPqlForm(true)}
          color="primary"
          style={{
            transform: 'scale(0.6)',
            transformOrigin: 'right',
            padding: 0,
            marginBottom: 4,
            marginLeft: -8,
          }}
        >
          <Edit />
        </IconButton>
      )}
    </div>
  );

  return (
    <Paper
      elevation={2}
      style={
        mobileView
          ? {
              padding: 8,
              marginTop: 8,
              marginBottom: 8,
            }
          : {
              padding: 8,
              marginTop: 8,
              marginBottom: 8,
              minWidth: readonly ? 300 : 550,
              height: 86,
            }
      }
    >
      <div
        style={{
          padding: 8,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          height: '100%',
        }}
      >
        <div
          style={
            mobileView
              ? {
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  marginRight: 16,
                  position: 'absolute',
                  top: 8,
                  justifyContent: 'center',
                }
              : {
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  marginRight: 16,
                }
          }
        >
          <Tire fill={theme.palette.primary.main} style={{ width: 30, height: 30 }} />
          {t('Config {{comboNumber}}', {comboNumber})}
          {readonly && <>{qtySelectorComponent}</>}
        </div>
        <ul
          style={
            mobileView
              ? {
                  fontSize: 11,
                  margin: 0,
                  marginTop: 46,
                  width: '100%',
                  padding: 0,
                }
              : {
                  fontSize: 11,
                  margin: 0,
                  position: 'absolute',
                  right: readonly ? 16 : 180,
                }
          }
        >
          <li
            style={{
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {t('Mount to Rims')}
            {prices.installation && option.installation && Number(prices.installation.invoice_price) > 0 && (
              <>
                <Taxes
                  compactMode
                  taxes={getTAXES(prices.installation, option.qty)}
                  totalTax={getTotalPartTAX(prices.installation, option.qty)}
                />
                {t(`$${(Number(prices.installation.invoice_price) * option.qty).toFixed(2)}`)}
              </>
            )}
            {option.installation && <CheckCircle color="primary" style={{ width: 16, marginLeft: 4 }} />}
            {!option.installation && <Cancel color="error" style={{ width: 16, marginLeft: 4 }} />}
          </li>
          <li
            style={{
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {t('New Rims/Wheels')}
            {option.wheels && prices.wheels && instanceOfPqlPrices(prices.wheels) && (
              <>
                {!!pqlPrice && <>{pqlPriceRender}</>}
                {!pqlPrice && !isDealer && (
                  <div style={{ marginLeft: 8, color: theme.palette.warning.main }}>{t('Required')}</div>
                )}
                {!pqlPrice && isDealer && (
                  <div style={{ marginLeft: 8, color: theme.palette.warning.main }}>{requestPql}</div>
                )}
              </>
            )}
            {prices.wheels &&
              option.wheels &&
              !instanceOfPqlPrices(prices.wheels) &&
              Number(prices.wheels.invoice_price) > 0 && (
                <>
                  <Taxes
                    compactMode
                    taxes={getTAXES(prices.wheels, option.qty)}
                    totalTax={getTotalPartTAX(prices.wheels, option.qty)}
                  />
                  {t(`$${(Number(prices.wheels.invoice_price) * option.qty).toFixed(2)}`)}
                </>
              )}
            {option.wheels && <CheckCircle color="primary" style={{ width: 16, marginLeft: 4 }} />}
            {!option.wheels && <Cancel color="error" style={{ width: 16, marginLeft: 4 }} />}
          </li>
          <li
            style={{
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {t('TPMS Rebuild Kit')}
            {prices.tpms && option.tpms && Number(prices.tpms.invoice_price) > 0 && (
              <>
                <Taxes
                  compactMode
                  taxes={getTAXES(prices.tpms, option.qty)}
                  totalTax={getTotalPartTAX(prices.tpms, option.qty)}
                />
                {t(`$${(Number(prices.tpms.invoice_price) * option.qty).toFixed(2)}`)}
              </>
            )}
            {option.tpms && <CheckCircle color="primary" style={{ width: 16, marginLeft: 4 }} />}
            {!option.tpms && <Cancel color="error" style={{ width: 16, marginLeft: 4 }} />}
          </li>
          {!!total && total > 0 && !missingPrice && (
            <li
              style={{
                height: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <div style={{ marginTop: 16, fontWeight: 'bold' }}>
                {total && t('TOTAL INSTALLATION: ${{total}}', {total: total.toFixed(2)})}
              </div>
            </li>
          )}
        </ul>
        {!readonly && (
          <div
            style={
              mobileView
                ? {
                    position: 'absolute',
                    right: -8,
                    top: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }
                : {
                    position: 'absolute',
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                  }
            }
          >
            <div
              style={{
                transform: 'scale(0.6)',
                transformOrigin: 'center right',
              }}
            >
              {qtySelectorComponent}
            </div>
            {deleteOptionComponent}
            <IconButton color="primary" onClick={onOpen} style={{ paddingLeft: 0 }}>
              <Edit />
            </IconButton>
          </div>
        )}
      </div>
    </Paper>
  );
}
